import { Check, Star } from 'lucide-react'
import { BottomSheet } from '@tendbot/ui'

interface AccountOption {
  connectionId: string
  displayName: string
  isDefault: boolean
  provider?: string
}

const PROVIDER_LABELS: Record<string, string> = {
  'google': 'Gmail',
  'microsoft-365': 'Outlook',
  'tendbot': 'TendBot',
}

interface AccountSheetProps {
  open: boolean
  title: string
  accounts: AccountOption[]
  selectedId: string | undefined
  onSelect: (connectionId: string) => void
  onClose: () => void
}

/** Color from a hash of the display name */
function avatarColor(name: string): string {
  const colors = ['#D4A853', '#6B8AED', '#E87D7D', '#7DD4A8', '#C07DDB', '#E8A84C'] as const
  let hash = 0
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash)
  return colors[Math.abs(hash) % colors.length] ?? '#D4A853'
}

export default function AccountSheet({ open, title, accounts, selectedId, onSelect, onClose }: AccountSheetProps) {
  return (
    <BottomSheet
      open={open}
      onOpenChange={(o) => { if (!o) onClose() }}
      title={title}
      showTitle
      className="pb-safe"
    >
      <div className="px-3 pb-5 space-y-1">
        {accounts.map(account => {
          const isSelected = account.connectionId === selectedId
          const initial = account.displayName.charAt(0).toUpperCase()
          const color = avatarColor(account.displayName)

          return (
            <button
              key={account.connectionId}
              type="button"
              onClick={() => {
                onSelect(account.connectionId)
                onClose()
              }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors ${
                isSelected
                  ? 'bg-gold/10 border border-gold/20'
                  : 'hover:bg-white/5 border border-transparent'
              }`}
            >
              <span
                className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-midnight shrink-0"
                style={{ backgroundColor: color }}
              >
                {initial}
              </span>
              <div className="flex-1 text-left min-w-0">
                <span className="text-sm text-ivory truncate block">{account.displayName}</span>
                {account.provider && PROVIDER_LABELS[account.provider] && (
                  <span className="text-[10px] text-pearl/60">{PROVIDER_LABELS[account.provider]}</span>
                )}
              </div>
              {account.isDefault && (
                <Star size={12} className="text-gold shrink-0" fill="currentColor" />
              )}
              {isSelected && (
                <Check size={16} className="text-gold shrink-0" />
              )}
            </button>
          )
        })}
      </div>
    </BottomSheet>
  )
}
